import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  buildHomeSearchCatalog,
  filterHomeSearchCatalog,
  type HomeSearchAction,
  type HomeSearchCatalogEntry,
  type HomeSearchVisual,
} from '../homeSearchCatalog';
import type { Service } from './ServiceCard';
import type { AppointmentTaker } from '../types';
import type { HomeTopicSectionConfig } from '../homeTopicSections';

const SEARCH_PLACEHOLDER_HINTS = [
  'Search for immigration services…',
  'Search RCIC consultants…',
  'Search tax & office services…',
  'Search for lawyers…',
  'Search our services…',
];

const HINT_INTERVAL_MS = 3800;

function SearchResultVisual({ visual }: { visual: HomeSearchVisual }) {
  if (visual.type === 'image') {
    return (
      <span className="home-search-bar__result-visual home-search-bar__result-visual--image">
        <img src={visual.src} alt={visual.alt ?? ''} loading="lazy" decoding="async" />
      </span>
    );
  }
  if (visual.type === 'initials') {
    return (
      <span
        className="home-search-bar__result-visual home-search-bar__result-visual--initials"
        style={{ background: visual.background }}
        aria-hidden
      >
        {visual.text}
      </span>
    );
  }
  const variantClass =
    visual.variant === 'soft'
      ? 'home-search-bar__result-visual--icon-soft'
      : 'home-search-bar__result-visual--icon-red';
  return (
    <span
      className={`home-search-bar__result-visual home-search-bar__result-visual--icon ${variantClass}`}
      aria-hidden
    >
      <i className={`ti ${visual.iconClass}`} />
    </span>
  );
}

export interface HomeSearchBarProps {
  topicSections?: HomeTopicSectionConfig[];
  storeServices: Service[];
  consultants: AppointmentTaker[];
  onTopicSelect: (categoryId: string, slug: string) => void;
}

export const HomeSearchBar: React.FC<HomeSearchBarProps> = ({
  topicSections,
  storeServices,
  consultants,
  onTopicSelect,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [hintIndex, setHintIndex] = useState(0);
  const [focused, setFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const reduceMotion = useReducedMotion();
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const blurCloseTimer = useRef<number | null>(null);

  const catalog = useMemo(
    () =>
      buildHomeSearchCatalog({
        topicSections,
        storeServices,
        consultants,
      }),
    [topicSections, storeServices, consultants]
  );

  const results = useMemo(
    () => filterHomeSearchCatalog(catalog, search),
    [catalog, search]
  );

  const queryTrimmed = search.trim();
  const dropdownOpen = focused && queryTrimmed.length > 0;

  useEffect(() => {
    setSearch(searchParams.get('q') ?? '');
  }, [searchParams]);

  useEffect(() => {
    if (focused || search.trim()) return undefined;
    const timer = window.setInterval(() => {
      setHintIndex((i) => (i + 1) % SEARCH_PLACEHOLDER_HINTS.length);
    }, HINT_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [focused, search]);

  useEffect(() => {
    setHighlightIndex(-1);
  }, [search, results.length]);

  const runAction = useCallback(
    (action: HomeSearchAction) => {
      switch (action.type) {
        case 'topic':
          onTopicSelect(action.categoryId, action.slug);
          break;
        case 'store':
          navigate(`/store/${action.slug}`);
          break;
        case 'consultant':
          navigate(`/appointments/${action.slug}/book`);
          break;
        case 'scroll':
          document.getElementById(action.elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          break;
        default:
          break;
      }
    },
    [navigate, onTopicSelect]
  );

  const pickResult = useCallback(
    (row: HomeSearchCatalogEntry) => {
      const q = search.trim();
      navigate(q ? `/?q=${encodeURIComponent(q)}` : '/', { replace: true });
      runAction(row.action);
      setFocused(false);
      inputRef.current?.blur();
    },
    [navigate, runAction, search]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = search.trim();
    if (dropdownOpen && highlightIndex >= 0 && results[highlightIndex]) {
      pickResult(results[highlightIndex]);
      return;
    }
    if (dropdownOpen && results.length === 1) {
      pickResult(results[0]);
      return;
    }
    navigate(q ? `/?q=${encodeURIComponent(q)}` : '/', { replace: true });
    setFocused(false);
  };

  const showAnimatedHint = !search.trim() && !focused;
  const activeHint = SEARCH_PLACEHOLDER_HINTS[hintIndex];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!dropdownOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setFocused(false);
        inputRef.current?.blur();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setFocused(false);
      inputRef.current?.blur();
    }
  };

  const scheduleBlurClose = () => {
    blurCloseTimer.current = window.setTimeout(() => setFocused(false), 120);
  };

  const cancelBlurClose = () => {
    if (blurCloseTimer.current !== null) {
      window.clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
  };

  return (
    <section className="home-search-bar" aria-label="Search">
      <form className="home-search-bar__form" onSubmit={handleSubmit}>
        <div
          className={`home-search-bar__field${dropdownOpen ? ' home-search-bar__field--open' : ''}`}
        >
          <SearchIcon className="home-search-bar__icon" aria-hidden />
          {showAnimatedHint ? (
            <div className="home-search-bar__hint" aria-hidden>
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={activeHint}
                  className="home-search-bar__hint-text"
                  initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: -6 }}
                  transition={{ duration: reduceMotion ? 0 : 0.35, ease: [0.32, 0.72, 0, 1] }}
                >
                  {activeHint}
                </motion.span>
              </AnimatePresence>
            </div>
          ) : null}
          <input
            ref={inputRef}
            type="search"
            className="home-search-bar__input"
            placeholder={showAnimatedHint ? ' ' : 'Search'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => {
              cancelBlurClose();
              setFocused(true);
            }}
            onBlur={scheduleBlurClose}
            onKeyDown={handleKeyDown}
            aria-label="Search services, immigration, RCIC, lawyers, and more"
            aria-expanded={dropdownOpen}
            aria-controls={dropdownOpen ? listboxId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              dropdownOpen && highlightIndex >= 0
                ? `${listboxId}-option-${highlightIndex}`
                : undefined
            }
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            role="combobox"
          />

          {dropdownOpen ? (
            <div
              id={listboxId}
              className="home-search-bar__dropdown"
              role="listbox"
              onMouseDown={(e) => e.preventDefault()}
            >
              {results.length === 0 ? (
                <p className="home-search-bar__empty">No matches on this page</p>
              ) : (
                <ul className="home-search-bar__results" role="presentation">
                  {results.map((row, index) => (
                    <li key={row.id} role="presentation">
                      <button
                        type="button"
                        id={`${listboxId}-option-${index}`}
                        role="option"
                        aria-selected={index === highlightIndex}
                        className={`home-search-bar__result${
                          index === highlightIndex ? ' home-search-bar__result--active' : ''
                        }`}
                        onMouseEnter={() => setHighlightIndex(index)}
                        onClick={() => pickResult(row)}
                      >
                        <SearchResultVisual visual={row.visual} />
                        <span className="home-search-bar__result-body">
                          <span className="home-search-bar__result-title">{row.title}</span>
                          <span className="home-search-bar__result-meta">
                            {row.group}
                            {row.subtitle ? ` · ${row.subtitle}` : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      </form>
    </section>
  );
};
